// src/pages/AIChatPage.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FaRobot,
  FaUser,
  FaPaperPlane,
  FaPlus,
  FaTrash,
  FaChevronDown,
  FaRegCopy,
  FaCheck,
  FaFilePdf,
  FaSyncAlt,
  FaTimes,
} from "react-icons/fa";

import api, { sendToAI } from "../utils/api";

/* ===========================================================
 🧠 Tool Titles
=========================================================== */
const sections = {
  analyzer: "تحليل العقود",
  draft: "توليد البنود والصياغات",
  advisor: "المستشار القانوني الذكي",
  summarizer: "ملخص القضايا والمذكرات",
  smartContracts: "توليد العقود الذكية",
};

const ALLOWED_MIMES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "text/plain",
];

const safeTrim = (v) => String(v ?? "").trim();

const getOrCreateUserId = () => {
  let userId = localStorage.getItem("userId");
  if (!userId) {
    userId = `anon_${Date.now()}`;
    localStorage.setItem("userId", userId);
  }
  return userId;
};

const isAnonId = (id) => String(id || "").startsWith("anon_");

const Badge = ({ children, className = "" }) => (
  <span
    className={
      "px-2 py-1 rounded-full text-[11px] sm:text-xs font-bold bg-yellow-500/15 text-yellow-200 border border-yellow-400/20 " +
      className
    }
  >
    {children}
  </span>
);

/* ===========================================================
  Modal: Paid blocked -> switch to free
=========================================================== */
function SwitchPlanModal({ open, title, message, onClose, onSwitchToFree, contact }) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center p-3">
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative w-full max-w-lg rounded-2xl border border-yellow-400/20 bg-gray-900/95 shadow-2xl p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-yellow-200 font-extrabold text-lg">{title}</div>
            <div className="text-gray-200 text-sm mt-2 whitespace-pre-wrap leading-relaxed">
              {message}
            </div>
          </div>
          <button
            className="text-gray-300 hover:text-white p-2 rounded-lg bg-white/5 border border-white/10"
            onClick={onClose}
            title="إغلاق"
          >
            <FaTimes />
          </button>
        </div>

        <div className="mt-5 flex flex-col sm:flex-row gap-2">
          <button
            className="flex-1 bg-gradient-to-br from-yellow-500 to-yellow-600 text-black font-bold px-4 py-3 rounded-xl shadow-lg hover:shadow-yellow-400/20 transition"
            onClick={onSwitchToFree}
          >
            ✅ حوّل للمجاني وكمل
          </button>
          <button
            className="flex-1 bg-gray-800 text-gray-100 font-bold px-4 py-3 rounded-xl border border-yellow-400/15 hover:bg-gray-700 transition"
            onClick={onClose}
          >
            تمام
          </button>
        </div>



{contact?.phone && (
  <div className="mt-3 flex flex-col sm:flex-row gap-2">
    <a
      href={`tel:${contact.phone}`}
      className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white font-bold px-4 py-3 rounded-xl text-center transition"
    >
      📞 اتصال
    </a>

    <a
      href={`https://wa.me/${contact.whatsapp || contact.phone}`}
      target="_blank"
      rel="noopener noreferrer"
      className="flex-1 bg-green-600 hover:bg-green-500 text-white font-bold px-4 py-3 rounded-xl text-center transition"
    >
      💬 واتساب
    </a>
  </div>
)}



        <div className="mt-4 text-xs text-gray-300">
          💡 البرو هيفضل مقفول لحد ما الإدارة تزود رسائل/تفعيل لحسابك.
        </div>
      </div>
    </div>
  );
}

/* ===========================================================
  Modal: PDF Export (All / Selected)
=========================================================== */
function PdfExportModal({
  open,
  onClose,
  onPrintAll,
  onEnableSelect,
  isSelecting,
  selectedCount,
  onPrintSelected,
  onClearSelection,
}) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center p-3">
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative w-full max-w-lg rounded-2xl border border-yellow-400/20 bg-gray-900/95 shadow-2xl p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-yellow-200 font-extrabold text-lg">
              📄 طباعة المحادثة PDF
            </div>
            <div className="text-gray-200 text-sm mt-2 whitespace-pre-wrap leading-relaxed">
              اختار إنت عايز تطبع الكل ولا رسايل معينة.
            </div>
          </div>

          <button
            className="text-gray-300 hover:text-white p-2 rounded-lg bg-white/5 border border-white/10"
            onClick={onClose}
            title="إغلاق"
          >
            <FaTimes />
          </button>
        </div>

        <div className="mt-5 space-y-2">
          <button
            className="w-full bg-gradient-to-br from-red-500 to-red-600 text-white font-bold px-4 py-3 rounded-xl shadow-lg hover:shadow-red-400/20 transition"
            onClick={onPrintAll}
          >
            ✅ طباعة الشات كله
          </button>

          {!isSelecting ? (
            <button
              className="w-full bg-gray-800 text-gray-100 font-bold px-4 py-3 rounded-xl border border-yellow-400/15 hover:bg-gray-700 transition"
              onClick={onEnableSelect}
            >
              🎯 طباعة رسايل معينة (اختارها)
            </button>
          ) : (
            <div className="space-y-2">
              <div className="text-xs text-gray-300">
                🔎 وضع التحديد شغال — المحدد دلوقتي:{" "}
                <span className="text-yellow-200 font-bold">
                  {selectedCount}
                </span>
              </div>

              <button
                className="w-full bg-gradient-to-br from-yellow-500 to-yellow-600 text-black font-bold px-4 py-3 rounded-xl shadow-lg hover:shadow-yellow-400/20 transition disabled:opacity-60 disabled:cursor-not-allowed"
                onClick={onPrintSelected}
                disabled={selectedCount === 0}
              >
                🖨️ طباعة المحدد
              </button>

              <button
                className="w-full bg-gray-800 text-gray-100 font-bold px-4 py-3 rounded-xl border border-yellow-400/15 hover:bg-gray-700 transition"
                onClick={onClearSelection}
              >
                🧹 إلغاء التحديد
              </button>
            </div>
          )}
        </div>

        <div className="mt-4 text-xs text-gray-300">
          💡 لو اخترت “طباعة رسايل معينة” هتظهر Checkboxes جنب كل رسالة.
        </div>
      </div>
    </div>
  );
}

export default function AIChatPage() {
  const { tool } = useParams();
  const sectionName = sections[tool] || "الذكاء الاصطناعي";

  const token = localStorage.getItem("token") || "";
  const storedUserId = localStorage.getItem("userId") || "";
  const userId = useMemo(
    () => storedUserId || getOrCreateUserId(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [storedUserId]
  );

  const usedTool = useMemo(() => safeTrim(tool) || "advisor", [tool]);

  const [activeSessionId, setActiveSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);

  const [messages, setMessages] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);

  const [input, setInput] = useState("");
  const [file, setFile] = useState(null);

  const [sending, setSending] = useState(false);
  const [aiTyping, setAiTyping] = useState(false);

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState("العادي");

  const [copiedIndex, setCopiedIndex] = useState(null);
  const [exportingPdf, setExportingPdf] = useState(false);

  // ✅ Desktop sidebar always visible; Mobile uses drawer
  const [sidebarOpen, setSidebarOpen] = useState(false); // mobile default closed

  // ✅ modal state for paid-blocked
  const [switchModal, setSwitchModal] = useState({
    open: false,
    title: "⚠️ البرو غير متاح",
    message: "",
    cachedPayload: null, // { text, file }
  });

  // ✅ PDF modal + selection
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [selectingPdfMessages, setSelectingPdfMessages] = useState(false);
  const [selectedMsgKeys, setSelectedMsgKeys] = useState(() => new Set());

  // optional: show tiny meta (provider/model)
  const [lastMeta, setLastMeta] = useState(null);

  const chatEndRef = useRef(null);

  /* ===========================================================
   📜 Auto-scroll
  ============================================================ */
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, aiTyping]);

  /* ===========================================================
   🧠 Sessions API
   - للمسجلين فقط (userId مش anon_)
   - anon: local only
  ============================================================ */
  const canUseDbSessions = useMemo(() => !isAnonId(userId), [userId]);

  const fetchSessions = async ({ selectFirst = true } = {}) => {
    // لو زائر: سيشن محلي فقط
    if (!canUseDbSessions) {
      const sid = activeSessionId || Date.now().toString();
      setActiveSessionId(sid);
      setMessages([]);
      setSessions([]);
      return;
    }

    // لازم يكون token عشان routes protected
    if (!token) {
      // اعتبره local-only (مفيش JWT)
      const sid = activeSessionId || Date.now().toString();
      setActiveSessionId(sid);
      setMessages([]);
      setSessions([]);
      return;
    }

    setSessionsLoading(true);
    try {
      const res = await api.get("/ai/sessions", {
        params: { userId, tool: usedTool },
      });
      const list = res?.data?.sessions || [];
      setSessions(list);

      if (selectFirst) {
        if (list.length) {
          const first = list[0];
          const sid = first.sessionId || first._id;
          setActiveSessionId(sid);
          await fetchChatById(sid);
        } else {
          const sid = Date.now().toString();
          setActiveSessionId(sid);
          setMessages([]);
        }
      }
    } catch (e) {
      console.error("❌ fetchSessions:", e);
      const sid = activeSessionId || Date.now().toString();
      setActiveSessionId(sid);
      setMessages([]);
      setSessions([]);
    } finally {
      setSessionsLoading(false);
    }
  };

  const fetchChatById = async (id) => {
    if (!id || !canUseDbSessions || !token) return;
    setChatLoading(true);
    try {
      const res = await api.get(`/ai/sessions/${id}`, { params: { userId } });
      const session = res?.data?.session;
      setMessages(session?.messages || []);
    } catch (e) {
      console.error("❌ fetchChatById:", e);
      setMessages([]);
    } finally {
      setChatLoading(false);
    }
  };

  useEffect(() => {
    setActiveSessionId(null);
    setMessages([]);
    setSessions([]);
    setSelectingPdfMessages(false);
    setSelectedMsgKeys(new Set());
    setIsDropdownOpen(false);
    fetchSessions({ selectFirst: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usedTool]);

  // Close drawer when tool changes (mobile UX)
  useEffect(() => {
    setSidebarOpen(false);
  }, [usedTool]);

  /* ===========================================================
   🆕 New Session
  ============================================================ */
  const createNewSession = () => {
    const sid = Date.now().toString();
    setActiveSessionId(sid);
    setMessages([]);
    setInput("");
    setFile(null);
    setIsDropdownOpen(false);
    setLastMeta(null);

    setSelectingPdfMessages(false);
    setSelectedMsgKeys(new Set());
    setSidebarOpen(false); // close drawer on mobile
  };

  /* ===========================================================
   📂 Open Session
  ============================================================ */
  const openSession = async (sid) => {
    setActiveSessionId(sid);
    setIsDropdownOpen(false);
    setLastMeta(null);

    setSelectingPdfMessages(false);
    setSelectedMsgKeys(new Set());

    setSidebarOpen(false); // close drawer on mobile
    await fetchChatById(sid);
  };

  /* ===========================================================
   🗑️ Delete Session (DB only)
  ============================================================ */
  const deleteSession = async (sid) => {
    if (!canUseDbSessions || !token) return;
    if (!sid) return;
    if (!window.confirm("هل تريد حذف هذه المحادثة؟")) return;

    try {
      await api.delete(`/ai/sessions/${sid}`, {
        params: { userId, tool: usedTool },
      });
      await fetchSessions({ selectFirst: true });
    } catch (e) {
      console.error("❌ deleteSession:", e);
      alert("❌ فشل حذف المحادثة.");
    }
  };

  /* ===========================================================
   📎 File
  ============================================================ */
  const handleFileChange = (e) => {
    const selected = e.target.files?.[0];
    if (!selected) {
      setFile(null);
      return;
    }

    if (!ALLOWED_MIMES.includes(selected.type)) {
      alert("نوع الملف غير مدعوم. الأنواع: PDF, DOCX, صور, TXT");
      e.target.value = null;
      setFile(null);
      return;
    }

    const maxMB = 25;
    const sizeMB = selected.size / (1024 * 1024);
    if (sizeMB > maxMB) {
      alert(`الملف كبير جدًا. الحد الأقصى ${maxMB}MB`);
      e.target.value = null;
      setFile(null);
      return;
    }

    setFile(selected);
  };

  const clearFile = () => setFile(null);

  /* ===========================================================
   🧼 Clean AI
  ============================================================ */
  const cleanAiResponse = (response) => {
    if (!response) return "";
    return String(response).replace(/\r/g, "").trim();
  };

  /* ===========================================================
   📌 Stable key for selecting messages
  ============================================================ */
  const getMsgKey = (msg, index) => {
    if (msg?._id) return String(msg._id);
    return `${msg?.sender || "x"}_${msg?.createdAt || msg?.timestamp || "t"}_${index}`;
  };

  /* ===========================================================
   📋 Copy (preserve formatting)
  ============================================================ */
  const copyText = async (text, index) => {
    const t = String(text || "");

    const escapeHtml = (s) =>
      s
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");

    const html = `<pre style="white-space:pre-wrap; font-family:inherit; margin:0;">${escapeHtml(
      t
    )}</pre>`;

    try {
      if (navigator.clipboard && window.ClipboardItem) {
        const item = new window.ClipboardItem({
          "text/plain": new Blob([t], { type: "text/plain" }),
          "text/html": new Blob([html], { type: "text/html" }),
        });
        await navigator.clipboard.write([item]);
      } else {
        await navigator.clipboard.writeText(t);
      }

      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 900);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = t;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);

      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 900);
    }
  };

  /* ===========================================================
   📄 Export PDF (All / Selected)
  ============================================================ */
  const downloadChatPdf = async ({ onlySelected = false } = {}) => {
    const sessionId = activeSessionId;
    if (!sessionId) return alert("⚠️ لا توجد جلسة نشطة.");
    if (!messages?.length) return alert("⚠️ لا توجد رسائل لتحميلها.");
    if (!token) return alert("⚠️ لازم تسجل دخول عشان تحمّل PDF.");

    let exportMessages = messages;

    if (onlySelected) {
      const keys = selectedMsgKeys;
      exportMessages = (messages || []).filter((m, i) => keys.has(getMsgKey(m, i)));
      if (!exportMessages.length) return alert("⚠️ لازم تختار رسالة واحدة على الأقل.");
    }

    try {
      setExportingPdf(true);

      const payload = {
        sessionId,
        tool: usedTool,
        sectionName,
        messages: exportMessages.map((m) => ({
          sender: m.sender,
          text: m.text,
          createdAt: m.createdAt || m.timestamp || new Date().toISOString(),
        })),
      };

      const res = await api.post("/ai/export-pdf", payload, {
        responseType: "blob",
      });

      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = `avocato-chat-${usedTool}-${sessionId}${onlySelected ? "-selected" : ""}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error("❌ export-pdf:", e);
      const msg =
        e?.response?.data?.error ||
        e?.response?.data?.message ||
        e?.message ||
        "فشل إنشاء PDF";
      alert(`❌ ${msg}`);
    } finally {
      setExportingPdf(false);
    }
  };

  /* ===========================================================
   💬 Send Message Core
=========================================================== */
  const sendMessageCore = async ({ overridePlan, overrideText, overrideFile } = {}) => {
    if (sending) return;

    const sessionId = activeSessionId || Date.now().toString();
    const text = safeTrim(overrideText ?? input);
    const chosenPlan = overridePlan || selectedPlan;
    const chosenFile = overrideFile ?? file;

    const msgToSend = text || (chosenFile ? "حلل المستند المرفق وأجب بناءً عليه." : "");
    if (!msgToSend && !chosenFile) return;

    setSending(true);
    setAiTyping(true);

    const userMsg = {
      sender: "user",
      text: text || (chosenFile ? `📎 ملف مرفق: ${chosenFile.name}` : ""),
      createdAt: new Date().toISOString(),
    };

    const optimistic = [...(messages || []), userMsg];
    setMessages(optimistic);

    // clear input early (UX)
    setInput("");

    try {
      const result = await sendToAI({
        message: msgToSend,
        file: chosenFile,
        sessionId,
        tool: usedTool,
        selectedPlan: chosenPlan,
        userId,
      });

      const cleanedReply = cleanAiResponse(result?.reply);
      const aiMsg = {
        sender: "ai",
        text: cleanedReply,
        createdAt: new Date().toISOString(),
      };

      setMessages([...optimistic, aiMsg]);
      setActiveSessionId(result?.sessionId || sessionId);

      setLastMeta({
        plan: result?.plan,
        provider: result?.provider,
        model: result?.model,
        retrieval: result?.retrieval,
      });

      if (canUseDbSessions && token) {
        await fetchSessions({ selectFirst: false });
      }
    } catch (err) {
      const serverMsg =
        err?.response?.data?.message ||
        err?.message ||
        "❌ حدث خطأ أثناء إرسال الرسالة.";

      const code = err?.response?.data?.code;
const contact = err?.response?.data?.contact;
      
const allowSwitchToFree = !!err?.response?.data?.allowSwitchToFree;

      const paidBlockedCodes = [
        "PAID_LIMIT_REACHED",
        "PAID_NOT_ACTIVE",
        "PAID_ONLY_REGISTERED",
      ];

      if (paidBlockedCodes.includes(code) && allowSwitchToFree) {
        setSwitchModal({
          open: true,
          title: "⚠️ البرو غير متاح",
          message: `${serverMsg}\n\nتقدر تكمل على النسخة المجانية دلوقتي.`,
          cachedPayload: { text: msgToSend, file: chosenFile },
        });




if(code === "PRO_LIMIT_REACHED"){
  const phone = contact?.phone || "";
  const wa = contact?.whatsapp || "";

  setSwitchModal({
    open:true,
    title:"🚫 انتهت رسائل البرو",
    message:`
${serverMsg}

📞 اتصال مباشر
https://wa.me/${wa}

`,
    cachedPayload:null
  });

  return;
}



        const aiMsg = {
          sender: "ai",
          text: serverMsg,
          createdAt: new Date().toISOString(),
        };
        setMessages([...optimistic, aiMsg]);
        return;
      }

      const aiMsg = {
        sender: "ai",
        text: serverMsg,
        createdAt: new Date().toISOString(),
      };
      setMessages([...optimistic, aiMsg]);
    } finally {
      setAiTyping(false);
      setSending(false);
      setFile(null);
      setIsDropdownOpen(false);
    }
  };

  const sendMessage = async () => sendMessageCore();

  /* ===========================================================
   🧠 Sessions UI data
  ============================================================ */
  const sessionsUI = useMemo(() => {
    return (sessions || []).map((s) => {
      const sid = s.sessionId || s._id;
      const last = s.messages?.[s.messages.length - 1]?.text || "لا توجد رسائل بعد";
      const title = s.title || "محادثة";
      return { sid, title, last, updatedAt: s.updatedAt };
    });
  }, [sessions]);

  // Close dropdown when clicking outside (better mobile UX)
  useEffect(() => {
    const onDoc = (e) => {
      const t = e.target;
      if (!t?.closest?.("[data-plan-dropdown-root]")) setIsDropdownOpen(false);
    };
    document.addEventListener("click", onDoc);
    return () => document.removeEventListener("click", onDoc);
  }, []);

  return (
    <div className="relative h-[100svh] font-[Cairo] bg-gradient-to-br from-black via-gray-900 to-yellow-900 overflow-hidden">
      {/* ✅ Switch-to-free modal */}
      <SwitchPlanModal
        open={switchModal.open}
        title={switchModal.title}
        message={switchModal.message}
        onClose={() =>
          setSwitchModal({ open: false, title: "", message: "", cachedPayload: null })
        }
        onSwitchToFree={() => {
          setSelectedPlan("العادي");
          const payload = switchModal.cachedPayload;

          setSwitchModal({ open: false, title: "", message: "", cachedPayload: null });

          if (payload) {
            setTimeout(() => {
              sendMessageCore({
                overridePlan: "العادي",
                overrideText: payload.text,
                overrideFile: payload.file,
              });
            }, 120);
          }
        }}
      />

      {/* ✅ PDF modal */}
      <PdfExportModal
        open={pdfModalOpen}
        onClose={() => setPdfModalOpen(false)}
        onPrintAll={() => {
          setPdfModalOpen(false);
          setSelectingPdfMessages(false);
          setSelectedMsgKeys(new Set());
          downloadChatPdf({ onlySelected: false });
        }}
        onEnableSelect={() => {
          setSelectingPdfMessages(true);
        }}
        isSelecting={selectingPdfMessages}
        selectedCount={selectedMsgKeys.size}
        onPrintSelected={() => {
          setPdfModalOpen(false);
          downloadChatPdf({ onlySelected: true });
        }}
        onClearSelection={() => {
          setSelectedMsgKeys(new Set());
          setSelectingPdfMessages(false);
        }}
      />

      {/* =========================
          Mobile overlay (when drawer open)
         ========================= */}
      <div
        className={`fixed inset-0 z-[60] bg-black/60 backdrop-blur-[2px] md:hidden transition-opacity ${
          sidebarOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        onClick={() => setSidebarOpen(false)}
      />

      <div className="flex h-full">
        {/* =========================
            Sidebar (Desktop) + Drawer (Mobile)
           ========================= */}
        <aside
          className={[
            "z-[70] bg-gray-900/90 border-r border-yellow-400/20 flex flex-col",
            "md:w-80 md:static md:translate-x-0 md:opacity-100 md:pointer-events-auto",
            "fixed top-0 bottom-0 right-0 w-[86%] max-w-[360px] transition-transform duration-300",
            sidebarOpen ? "translate-x-0" : "translate-x-full",
          ].join(" ")}
          aria-label="Sessions Sidebar"
        >
          <div className="p-4 flex items-center justify-between gap-2 border-b border-yellow-400/10">
            <div className="flex flex-col gap-1 min-w-0">
              <h3 className="text-yellow-300 font-bold text-base">المحادثات</h3>
              <div className="flex gap-2 items-center flex-wrap">
                <Badge>{sectionName}</Badge>

                <button
                  onClick={() => fetchSessions({ selectFirst: false })}
                  className="text-yellow-300/80 hover:text-yellow-200 text-xs flex items-center gap-1"
                  title="تحديث"
                >
                  <FaSyncAlt />
                  تحديث
                </button>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={createNewSession}
                className="bg-yellow-500 hover:bg-yellow-400 text-black px-3 py-2 rounded-xl flex items-center gap-2 font-bold transition"
                title="محادثة جديدة"
              >
                <FaPlus /> جديد
              </button>

              {/* Close button (mobile only) */}
              <button
                onClick={() => setSidebarOpen(false)}
                className="md:hidden text-gray-200 hover:text-white p-2 rounded-xl bg-white/5 border border-white/10"
                title="إغلاق"
              >
                <FaTimes />
              </button>
            </div>
          </div>

          <div className="px-4 py-3 text-xs text-gray-300">
            {sessionsLoading ? "⏳ جارٍ تحميل الجلسات..." : `${sessionsUI.length} جلسة`}
          </div>

          <div className="flex-1 overflow-auto space-y-2 px-3 pb-4 custom-scrollbar">
            {!token && canUseDbSessions && (
              <div className="text-gray-300 text-xs p-3 rounded-xl border border-yellow-400/10 bg-black/20">
                ⚠️ لازم <span className="font-bold text-yellow-200">تسجل دخول</span> عشان
                المحادثات تتخزن وتظهر هنا.
              </div>
            )}

            {!canUseDbSessions && (
              <div className="text-gray-300 text-xs p-3 rounded-xl border border-yellow-400/10 bg-black/20">
                ⚠️ أنت داخل كـ <span className="font-bold text-yellow-200">زائر</span>.
                المحادثات مش هتتخزن في السيرفر.
              </div>
            )}

            {sessionsUI.length === 0 && !sessionsLoading && canUseDbSessions && token && (
              <div className="text-gray-400 text-sm p-3 rounded-xl border border-yellow-400/10 bg-black/20">
                {"لا توجد محادثات محفوظة بعد. اضغط "}
                <span className="font-bold text-yellow-200">جديد</span>
                {" لبدء محادثة."}
              </div>
            )}

            {sessionsUI.map((s) => (
              <div
                key={s.sid}
                onClick={() => openSession(s.sid)}
                className={`p-3 rounded-xl cursor-pointer flex justify-between gap-2 border ${
                  s.sid === activeSessionId
                    ? "bg-yellow-600/15 border-yellow-400/30"
                    : "hover:bg-yellow-600/10 border-yellow-400/10"
                }`}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-yellow-200 font-semibold text-sm truncate">
                    {s.title}
                  </div>
                  <div className="text-xs text-gray-300 truncate mt-1">{s.last}</div>
                </div>

                {canUseDbSessions && token && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteSession(s.sid);
                    }}
                    className="text-red-400 hover:text-red-300 p-2"
                    title="حذف"
                  >
                    <FaTrash />
                  </button>
                )}
              </div>
            ))}
          </div>
        </aside>

        {/* =========================
            Main
           ========================= */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Header */}
          <header className="sticky top-0 z-50 bg-black/65 backdrop-blur-md border-b border-yellow-400/20 text-yellow-200 shadow-md">
            <div className="px-3 sm:px-4 py-3 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                {/* Mobile drawer toggle */}
                <button
                  onClick={() => setSidebarOpen((v) => !v)}
                  className="md:hidden bg-yellow-500/10 hover:bg-yellow-500/15 border border-yellow-400/20 text-yellow-200 px-3 py-2 rounded-xl text-sm"
                  title="المحادثات"
                >
                  ☰
                </button>

                {/* Desktop: keep a small toggle if you want - optional */}
                <button
                  onClick={() => setSidebarOpen((v) => !v)}
                  className="hidden md:inline-flex bg-yellow-500/10 hover:bg-yellow-500/15 border border-yellow-400/20 text-yellow-200 px-3 py-2 rounded-xl text-sm"
                  title="فتح المحادثات على الموبايل"
                >
                  ☰
                </button>

                <div className="min-w-0">
                  <div className="font-extrabold text-base sm:text-lg truncate">
                    ⚖️ الأفوكاتو الذكي
                  </div>
                  <div className="flex gap-1.5 items-center flex-wrap mt-1">
                    <Badge className="hidden sm:inline-flex">{sectionName}</Badge>
                    <Badge>{selectedPlan === "البرو" ? "برو" : "مجاني"}</Badge>

                    {lastMeta?.provider && (
                      <span className="hidden lg:inline text-xs text-gray-300">
                        • {lastMeta.provider} / {lastMeta.model}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => setPdfModalOpen(true)}
                  disabled={exportingPdf}
                  className={`bg-gradient-to-br from-red-500 to-red-600 text-white px-3 sm:px-4 py-2 rounded-xl font-bold flex items-center gap-2 shadow-lg hover:shadow-red-400/30 transition ${
                    exportingPdf ? "opacity-60 cursor-not-allowed" : ""
                  }`}
                  title="تحميل المحادثة PDF"
                >
                  <FaFilePdf /> <span className="hidden sm:inline">{exportingPdf ? "جارٍ..." : "PDF"}</span>
                </button>

                {/* Dropdown root wrapper to detect outside click */}
                <div className="relative" data-plan-dropdown-root>
                  <motion.button
                    onClick={() => setIsDropdownOpen((v) => !v)}
                    className="bg-gradient-to-br from-blue-500 to-blue-600 text-white px-3 sm:px-4 py-2 rounded-xl font-bold flex items-center gap-2 shadow-lg hover:shadow-blue-400/40 transition"
                    whileTap={{ scale: 0.98 }}
                    title="اختيار الخطة"
                  >
                    <FaChevronDown />
                    <span className="hidden sm:inline">
                      {selectedPlan === "العادي" ? "المجانية" : "المدفوعة"}
                    </span>
                  </motion.button>

                  {isDropdownOpen && (
                    <div className="absolute left-0 mt-2 z-50 bg-gray-900 border border-yellow-500/30 p-2 rounded-xl text-yellow-200 shadow-2xl w-56">
                      <button
                        onClick={() => {
                          setSelectedPlan("العادي");
                          setIsDropdownOpen(false);
                        }}
                        className={`block w-full text-right p-2 rounded-lg hover:bg-gray-800 transition ${
                          selectedPlan === "العادي" ? "bg-gray-800" : ""
                        }`}
                      >
                        النسخة المجانية
                      </button>

                      <button
                        onClick={() => {
                          setSelectedPlan("البرو");
                          setIsDropdownOpen(false);
                        }}
                        className={`block w-full text-right p-2 rounded-lg hover:bg-gray-800 transition ${
                          selectedPlan === "البرو" ? "bg-gray-800" : ""
                        }`}
                      >
                        النسخة المدفوعة
                      </button>

                      <div className="mt-2 text-xs text-gray-300 p-2 border-t border-yellow-400/10">
                        💡 لو البرو اتقفل بسبب نفاد الرسائل، هتقدر تحوّل للمجاني وتكمل.
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </header>

          {/* Chat Body */}
          <div className="flex-1 overflow-y-auto px-3 sm:px-5 py-4 space-y-4">
            {chatLoading && (
              <div className="text-center text-gray-300 animate-pulse">
                ⏳ جارٍ تحميل الرسائل...
              </div>
            )}

            {!chatLoading && messages.length === 0 && (
              <div className="text-center text-gray-400 italic mt-12">
                ✍️ ابدأ المحادثة هنا... (ارفع ملف أو اكتب سؤالك)
              </div>
            )}

            {selectingPdfMessages && messages.length > 0 && (
              <div className="text-xs text-gray-300 p-3 rounded-xl border border-yellow-400/10 bg-black/20">
                ✅ وضع تحديد رسائل الطباعة شغال — اختار الرسائل اللي عايزها وبعدين اضغط PDF → طباعة المحدد.
              </div>
            )}

            <div className="mx-auto w-full max-w-4xl space-y-4">
              {messages.map((msg, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex ${msg.sender === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`relative w-full sm:w-auto sm:max-w-[80%] max-w-[92%] p-4 rounded-2xl shadow-md border ${
                      msg.sender === "user"
                        ? "bg-yellow-500 text-black border-yellow-400/30 rounded-br-none"
                        : "bg-gray-800 text-yellow-100 border-yellow-400/20 rounded-bl-none"
                    }`}
                  >
                    {/* ✅ Select for PDF */}
                    {selectingPdfMessages && (
                      <div className="absolute top-3 right-3">
                        <input
                          type="checkbox"
                          className="w-5 h-5 accent-yellow-500 cursor-pointer"
                          checked={selectedMsgKeys.has(getMsgKey(msg, i))}
                          onChange={(e) => {
                            const k = getMsgKey(msg, i);
                            setSelectedMsgKeys((prev) => {
                              const next = new Set(prev);
                              if (e.target.checked) next.add(k);
                              else next.delete(k);
                              return next;
                            });
                          }}
                          title="تحديد للطباعة"
                        />
                      </div>
                    )}

                    {/* Copy */}
                    <button
                      onClick={() => copyText(msg.text, i)}
                      className={`absolute top-3 left-3 text-xs px-2 py-1 rounded-lg border transition flex items-center gap-1 ${
                        msg.sender === "user"
                          ? "bg-black/10 border-black/20 hover:bg-black/20"
                          : "bg-yellow-500/10 border-yellow-400/20 hover:bg-yellow-500/20"
                      }`}
                      title="Copy"
                    >
                      {copiedIndex === i ? <FaCheck /> : <FaRegCopy />}
                      {copiedIndex === i ? "Copied" : "Copy"}
                    </button>

                    <div className="flex items-center gap-2 mb-2 text-sm font-semibold">
                      {msg.sender === "ai" ? (
                        <>
                          <FaRobot className="text-yellow-300" /> المستشار
                        </>
                      ) : (
                        <>
                          <FaUser className="text-yellow-200" /> أنت
                        </>
                      )}
                    </div>

                    <p className="whitespace-pre-wrap leading-relaxed text-[14px] sm:text-[15px]">
                      {msg.text}
                    </p>

                    {(msg.createdAt || msg.timestamp) && (
                      <div className="mt-3 text-[11px] opacity-70 text-left">
                        {new Date(msg.createdAt || msg.timestamp).toLocaleString("ar-EG")}
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}

              {aiTyping && (
                <div className="flex justify-start">
                  <div className="bg-gray-800 border border-yellow-400/20 text-yellow-100 rounded-2xl px-4 py-3 flex items-center gap-2 animate-pulse">
                    <FaRobot className="text-yellow-400" />
                    <div className="flex gap-1">
                      <span className="animate-bounce">•</span>
                      <span className="animate-bounce delay-150">•</span>
                      <span className="animate-bounce delay-300">•</span>
                    </div>
                  </div>
                </div>
              )}

              <div ref={chatEndRef} />
            </div>
          </div>

          {/* Composer */}
          <div className="sticky bottom-0 z-40 bg-black/70 backdrop-blur-md border-t border-yellow-400/20">
            <div className="p-3 sm:p-4 flex flex-col gap-3 mx-auto w-full max-w-4xl">
              {/* File Row */}
              <div className="flex flex-wrap items-center gap-2">
                <label className="cursor-pointer bg-gray-800 text-yellow-200 px-3 py-2 rounded-xl border border-yellow-400/30 text-sm hover:bg-gray-700 transition">
                  📎 إرفاق ملف
                  <input
                    type="file"
                    className="hidden"
                    onChange={handleFileChange}
                    accept=".pdf,.docx,.png,.jpg,.jpeg,.txt"
                  />
                </label>

                {file && (
                  <div className="flex items-center gap-2 text-xs text-gray-200 bg-black/30 border border-yellow-400/15 px-3 py-2 rounded-xl max-w-full sm:max-w-[520px]">
                    <span className="truncate">
                      <span className="font-bold text-yellow-200">مرفق:</span> {file.name}
                    </span>
                    <button
                      onClick={clearFile}
                      className="text-red-400 hover:text-red-300"
                      title="إزالة الملف"
                    >
                      <FaTrash />
                    </button>
                  </div>
                )}

                <div className="ml-auto text-[11px] sm:text-xs text-gray-300">
                  Session: <span className="text-yellow-200">{activeSessionId || "—"}</span>
                </div>
              </div>

              {/* Input Row */}
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="✍️ اكتب سؤالك القانوني هنا... (أو ارفع ملف فقط)"
                  className="flex-1 p-3 rounded-xl bg-gray-900/80 text-white border border-yellow-400/20 outline-none focus:ring-2 focus:ring-yellow-400/30 transition text-[14px] sm:text-[15px]"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") sendMessage();
                  }}
                  disabled={sending}
                />

                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={sendMessage}
                  disabled={sending || (!safeTrim(input) && !file)}
                  className={`bg-gradient-to-br from-yellow-500 to-yellow-600 text-black px-5 sm:px-6 py-3 rounded-xl font-bold flex items-center gap-2 shadow-lg hover:shadow-yellow-400/30 transition ${
                    sending || (!safeTrim(input) && !file) ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                >
                  <FaPaperPlane /> {sending ? "جاري..." : "إرسال"}
                </motion.button>
              </div>

              <div className="text-[11px] sm:text-xs text-gray-300">
                💡 تقدر ترفع <span className="font-bold text-yellow-200">PDF/DOCX/صورة/TXT</span>{" "}
                بالعربي، واكتب سؤالك عنه… والرد هيبقى مبني على المستند نفسه.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
